import moment from "moment";
import { useEffect, useState } from "react";
import { FiCheck, FiThumbsUp } from "react-icons/fi";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import { request } from "../helpers/request";
import StarRating from "./StarRating";

const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("-createdAt");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReviews();
  }, [productId, currentPage, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `${SummaryApi.getProductReviews.url}/${productId}?page=${currentPage}&limit=10&sort=${sortBy}`,
        method: SummaryApi.getProductReviews.method,
      });
      const data = await response.json();

      if (data.success) {
        setReviews(data.data || []);
        setStats(data.stats);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHelpful = async (reviewId) => {
    try {
      const response = await request({
        url: SummaryApi.markHelpful.url,
        method: SummaryApi.markHelpful.method,
        data: { reviewId },
      });

      const data = await response.json();

      if (data.success) {
        // Update the review locally
        setReviews(
          reviews.map((review) => {
            if (review._id === reviewId) {
              return {
                ...review,
                helpful: data.data.helpful,
                isHelpful: data.data.isHelpful,
              };
            }
            return review;
          }),
        );
      } else {
        toast.error(data.message || "Failed to mark as helpful");
      }
    } catch (error) {
      toast.error("Please login to mark reviews as helpful");
    }
  };

  const RatingBreakdown = () => {
    if (!stats) return null;

    const total = stats.totalReviews;
    const ratings = [
      { star: 5, count: stats.rating5 },
      { star: 4, count: stats.rating4 },
      { star: 3, count: stats.rating3 },
      { star: 2, count: stats.rating2 },
      { star: 1, count: stats.rating1 },
    ];

    return (
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-8">
          {/* Average Rating */}
          <div className="text-center">
            <div className="text-5xl font-bold text-gray-800 mb-2">
              {stats.averageRating.toFixed(1)}
            </div>
            <StarRating rating={stats.averageRating} size="lg" />
            <p className="text-sm text-gray-600 mt-2">
              {total} {total === 1 ? "Review" : "Reviews"}
            </p>
          </div>

          {/* Rating Bars */}
          <div className="flex-1">
            {ratings.map(({ star, count }) => {
              const percentage = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-3 mb-2">
                  <span className="text-sm text-gray-600 w-8">{star} ★</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>

      {stats && <RatingBreakdown />}

      {/* Sort Options */}
      {reviews.length > 0 && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-800">
            {stats?.totalReviews || 0} Reviews
          </h3>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="-createdAt">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="rating-high">Highest Rating</option>
            <option value="rating-low">Lowest Rating</option>
          </select>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">
            No reviews yet. Be the first to review this product!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review._id} className="border-b border-gray-200 pb-6">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-800">
                      {review.userId?.name || "Anonymous"}
                    </span>
                    {review.verified && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                        <FiCheck size={12} />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  <StarRating rating={review.rating} size="sm" />
                </div>
                <span className="text-sm text-gray-500">
                  {moment(review.createdAt).fromNow()}
                </span>
              </div>

              {/* Review Content */}
              <h4 className="font-semibold text-gray-800 mb-2">
                {review.title}
              </h4>
              <p className="text-gray-700 mb-3 whitespace-pre-line">
                {review.comment}
              </p>

              {/* Review Images */}
              {review.images && review.images.length > 0 && (
                <div className="flex gap-2 mb-3">
                  {review.images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`Review ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-md border border-gray-300 cursor-pointer hover:opacity-75 transition-opacity"
                      onClick={() =>
                        window.open(img, "_blank", "noopener,noreferrer")
                      }
                    />
                  ))}
                </div>
              )}

              {/* Helpful Button */}
              <button
                onClick={() => handleHelpful(review._id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  review.isHelpful
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FiThumbsUp size={14} />
                <span>Helpful ({review.helpful || 0})</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={!pagination.hasPrevPage}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-gray-700">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={!pagination.hasNextPage}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
